<p-dialog
  [visible]="visible"
  (visibleChange)="$event ? null : close()"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="true"
  appendTo="body"
  [baseZIndex]="10"
  styleClass="hsd-dialog"
  [style]="{ width: 'min(1000px, 96vw)' }">
  <ng-template pTemplate="header">
    <div class="hsd-head">
      <span class="hsd-head__icon"><i class="fa-solid fa-link" aria-hidden="true"></i></span>
      <div>
        <h2 class="hsd-head__title">Share documents with buyer</h2>
        <p class="hsd-head__sub">
          Select files from the project library and add an optional note. These files will be available in the buyer's Horizon account.
        </p>
      </div>
    </div>
  </ng-template>

  <div class="hsd">
    <div class="hsd__browser">
      <nav class="hsd__folders" aria-label="Folders">
        <button
          type="button"
          class="hsd__folder"
          *ngFor="let item of folders; trackBy: trackByKey"
          [class.hsd__folder--active]="folder === item.key"
          [attr.aria-current]="folder === item.key ? 'true' : null"
          (click)="setFolder(item.key)">
          <i [class]="folderIcon(item)" aria-hidden="true"></i>
          <span class="hsd__folder-name">{{ item.label }}</span>
          <span class="hsd__folder-count">{{ item.count }}</span>
        </button>
      </nav>

      <div class="hsd__main">
        <div class="hsd__toolbar">
          <label class="hsd__search">
            <i class="pi pi-search" aria-hidden="true"></i>
            <input
              type="text"
              placeholder="Search files or lots…"
              aria-label="Search files or lots"
              [(ngModel)]="search"
              (ngModelChange)="refresh()" />
          </label>
          <ng-select
            class="hsd__sort"
            [items]="sortOptions"
            bindLabel="label"
            bindValue="value"
            aria-label="Sort by"
            [searchable]="false"
            [clearable]="false"
            appendTo="body"
            [(ngModel)]="sort"
            (ngModelChange)="refresh()"></ng-select>
          <div class="hsd__views" role="group" aria-label="View">
            <button
              type="button"
              [class.active]="view === 'list'"
              aria-label="List view"
              [attr.aria-pressed]="view === 'list'"
              (click)="view = 'list'">
              <i class="pi pi-bars" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              [class.active]="view === 'grid'"
              aria-label="Grid view"
              [attr.aria-pressed]="view === 'grid'"
              (click)="view = 'grid'">
              <i class="pi pi-th-large" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div class="hsd__state" *ngIf="isLoading"><i class="pi pi-spin pi-spinner" aria-hidden="true"></i> Loading…</div>
        <div class="hsd__state hsd__state--error" *ngIf="loadError">
          The library could not be loaded.
          <button type="button" class="_outline-btn" (click)="load()">Retry</button>
        </div>

        <ng-container *ngIf="!isLoading && !loadError">
          <!-- List view -->
          <div class="hsd__table" *ngIf="view === 'list'" role="table" [class.hsd__table--dated]="hasDates">
            <div class="hsd__tr hsd__tr--head" role="row">
              <span role="columnheader">
                <p-checkbox
                  [binary]="true"
                  [ngModel]="allVisibleSelected"
                  [disabled]="!rows.length"
                  ariaLabel="Select all"
                  (onChange)="toggleAllVisible()"></p-checkbox>
              </span>
              <span role="columnheader">Name</span>
              <span role="columnheader">{{ isLotsFolder ? 'Availability' : 'Type' }}</span>
              <span role="columnheader">{{ isLotsFolder ? 'Price' : 'Size' }}</span>
              <span role="columnheader" *ngIf="hasDates">Updated</span>
            </div>
            <div class="hsd__rows">
              <div
                class="hsd__tr"
                role="row"
                *ngFor="let row of rows; trackBy: trackByKey"
                [class.hsd__tr--selected]="isSelected(row)"
                (click)="toggle(row)">
                <span role="cell" (click)="$event.stopPropagation()">
                  <p-checkbox [binary]="true" [ngModel]="isSelected(row)" [ariaLabel]="'Select ' + row.name" (onChange)="toggle(row)"></p-checkbox>
                </span>
                <span role="cell" class="hsd__name">
                  <span class="hsd__thumb">
                    <img *ngIf="thumbnail(row) as src; else rowIcon" [src]="src" alt="" loading="lazy" (error)="markBroken(row)" />
                    <ng-template #rowIcon><i [class]="'fa-regular ' + row.icon" aria-hidden="true"></i></ng-template>
                  </span>
                  <span class="hsd__name-text">
                    <strong [title]="row.name">{{ row.name }}</strong>
                    <small *ngIf="row.detail">{{ row.detail }}</small>
                    <ng-container *ngTemplateOutlet="floorplanOption; context: { $implicit: row }"></ng-container>
                  </span>
                </span>
                <span role="cell" class="hsd__muted">
                  <span class="hsd__badge" *ngIf="row.type === 'lot'; else typeCell">{{ row.badge || '—' }}</span>
                  <ng-template #typeCell>{{ row.typeLabel }}</ng-template>
                </span>
                <span role="cell" class="hsd__muted">
                  <ng-container *ngIf="row.type === 'lot'; else sizeCell">
                    {{ row.price ? (row.price | currency: 'AUD' : 'symbol-narrow' : '1.0-0') : '—' }}
                  </ng-container>
                  <ng-template #sizeCell>{{ row.sizeLabel || '—' }}</ng-template>
                </span>
                <span role="cell" class="hsd__muted" *ngIf="hasDates">{{ row.updated ? (row.updated | date: 'd MMM y') : '—' }}</span>
              </div>
              <p class="hsd__empty" *ngIf="!rows.length">{{ emptyText }}</p>
            </div>
          </div>

          <!-- Grid view -->
          <div class="hsd__grid" *ngIf="view === 'grid'">
            <div class="hsd__cell" *ngFor="let row of rows; trackBy: trackByKey">
              <button
                type="button"
                class="hsd__card"
                [class.hsd__card--selected]="isSelected(row)"
                [attr.aria-pressed]="isSelected(row)"
                (click)="toggle(row)">
                <span class="hsd__card-check" aria-hidden="true"><i class="pi pi-check"></i></span>
                <span class="hsd__card-media">
                  <img *ngIf="thumbnail(row) as src; else cardIcon" [src]="src" alt="" loading="lazy" (error)="markBroken(row)" />
                  <ng-template #cardIcon><i [class]="'fa-regular ' + row.icon" aria-hidden="true"></i></ng-template>
                </span>
                <strong class="hsd__card-name" [title]="row.name">{{ row.name }}</strong>
                <small class="hsd__muted" *ngIf="row.type === 'lot'"
                  >{{ row.detail }}<ng-container *ngIf="row.badge"> · {{ row.badge }}</ng-container></small
                >
                <small class="hsd__muted" *ngIf="row.type !== 'lot'"
                  >{{ row.typeLabel }}<ng-container *ngIf="row.sizeLabel"> · {{ row.sizeLabel }}</ng-container></small
                >
              </button>
              <ng-container *ngTemplateOutlet="floorplanOption; context: { $implicit: row }"></ng-container>
            </div>
            <p class="hsd__empty" *ngIf="!rows.length">{{ emptyText }}</p>
          </div>
        </ng-container>

        <!-- A ticked lot with a linked floor plan sends the plan too, unless the agent unticks it here. -->
        <ng-template #floorplanOption let-row>
          <span class="hsd__plan" *ngIf="showsFloorplanOption(row)" (click)="$event.stopPropagation()">
            <p-checkbox
              [binary]="true"
              [ngModel]="includesFloorplan(row)"
              [label]="row.floorplanName ? 'Include floor plan (' + row.floorplanName + ')' : 'Include floor plan'"
              [ariaLabel]="'Include the floor plan with ' + row.name"
              (onChange)="toggleFloorplan(row)"></p-checkbox>
          </span>
        </ng-template>
      </div>
    </div>

    <div class="hsd__note">
      <i class="fa-regular fa-file-lines hsd__note-icon" aria-hidden="true"></i>
      <div class="hsd__note-body">
        <label class="hsd__note-title" for="hsd-message">Add a note <span class="hsd__muted">(optional)</span></label>
        <p class="hsd__note-sub">This note will be included with the files in the buyer's Horizon account.</p>
        <div class="hsd__textarea">
          <textarea id="hsd-message" rows="4" [maxlength]="messageLimit" placeholder="Add a note for the buyer…" [(ngModel)]="message"></textarea>
          <small>{{ message.length }}/{{ messageLimit }}</small>
        </div>
      </div>
    </div>

    <div class="hsd__notify">
      <p-inputSwitch inputId="hsd-notify" [(ngModel)]="notifyEmail"></p-inputSwitch>
      <div>
        <label for="hsd-notify">Notify buyer by email</label>
        <p>
          The buyer is always notified in Horizon. When this is on, they will also receive an email that new items are available in their Horizon
          account.
        </p>
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="hsd-foot">
      <button type="button" class="_cancel-btn" (click)="close()">Cancel</button>
      <div class="hsd-foot__end">
        <span class="hsd-foot__selection" *ngIf="selectedCount">{{ selectionText }}</span>
        <button type="button" class="_primary-btn" [disabled]="isSending || !selectedCount" (click)="send()">
          <i class="pi pi-send" aria-hidden="true"></i> {{ isSending ? 'Sharing…' : 'Share with buyer' }}
        </button>
      </div>
    </div>
  </ng-template>
</p-dialog>
